import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography, Alert } from '@mui/material';
import DataTable, { Column } from './DataTable';
import { DEMO_TRANSACTIONS } from '../utils/demoTransactions';
import { transactionStore, PendingAmendment } from '../utils/transactionStore';

// Same demo-derivation used by the checker side, kept in sync for a maker-facing status view
const deriveDemoAmendments = (): PendingAmendment[] => {
  return DEMO_TRANSACTIONS.filter(txn => txn.amendStatus === 'PENDING' && txn.amendmentHistory && txn.amendmentHistory.length > 0).map(txn => {
    const latestAmendment = txn.amendmentHistory![txn.amendmentHistory!.length - 1];
    return {
      id: `AMEND-DEMO-${txn.id}`,
      transactionId: txn.id,
      mtoName: txn.mtoName,
      payoutType: txn.payoutType,
      amountFCY: txn.amountFCY,
      currencyFCY: txn.currencyFCY,
      currentState: txn.currentState,
      amendmentReason: 'Beneficiary account amendment',
      requestedBy: latestAmendment.requestedBy,
      requestedAt: latestAmendment.timestamp,
      status: 'PENDING' as const,
      originalAccount: latestAmendment.oldAccount,
      newAccount: latestAmendment.newAccount,
      fetchedTitle: latestAmendment.fetchedTitle
    };
  });
};
const MakerAmendmentTable: React.FC = () => {
  const [amendments, setAmendments] = useState<PendingAmendment[]>([]);
  const refresh = () => {
    setAmendments([...deriveDemoAmendments(), ...transactionStore.getPendingAmendments()]);
  };
  useEffect(() => {
    refresh();
    const unsubscribe = transactionStore.subscribe(refresh);
    return unsubscribe;
  }, []);
  const columns: Column<PendingAmendment>[] = [{
    id: 'id',
    label: 'Request ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1269" data-mp="30f4jq4kqw4gz">{row.id}</Typography>
  }, {
    id: 'transactionId',
    label: 'Transaction ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1270" data-mp="2qf42co6fryff">{row.transactionId}</Typography>
  }, {
    id: 'mtoName',
    label: 'MTO',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-1271" data-mp="2gue5fu03oe4x">{row.mtoName}</Typography>
  }, {
    id: 'newAccount',
    label: 'New Account',
    renderCell: row => <Typography variant="body2" fontFamily="monospace" fontWeight={600} color="primary.main" noWrap data-id="element-1272" data-mp="26ux0xfwl46aj">{row.newAccount.substring(0, 14)}...</Typography>
  }, {
    id: 'status',
    label: 'Status',
    renderCell: () => <Chip label="PENDING" size="small" sx={{
      backgroundColor: 'rgba(245, 158, 11, 0.1)',
      color: '#F59E0B',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-1273" data-mp="2zjkjv4tv2jkx" />
  }, {
    id: 'requestedAt',
    label: 'Submitted At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-1274" data-mp="2ps6kcx1wtjrs">{new Date(row.requestedAt).toLocaleString()}</Typography>
  }];
  return <Box data-id="element-1275" data-mp="3igvfmfgaauvm">
      <Alert severity="info" sx={{
      mb: 2,
      borderRadius: 2,
      fontSize: '0.82rem'
    }} data-id="element-1276" data-mp="3ih54u0ti8y09">
        Rejected amendments are returned to the original transaction, where they can be resubmitted via the Amend action.
      </Alert>
      <DataTable title="Pending Amendment Requests" data={amendments} columns={columns} keyField="id" emptyStateMessage="No pending amendment requests" initialVisibleColumns={['transactionId', 'mtoName', 'newAccount', 'status', 'requestedAt']} data-id="element-1277" data-mp="1brtpef751bry" />
    </Box>;
};
export default MakerAmendmentTable;